import React from 'react';
import productConfig from '../config/productConfig';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import PageMeta from '@/components/seo/PageMeta';

const PrivacyPolicy: React.FC = () => {
  const { brandPrefix, productType } = productConfig;
  const fullProductName = `${brandPrefix} ${productType}`;
  const companyName = 'Ginylil S.L.';
  const supportEmailNew = 'support@ginylil.com';

  return (
    <div className="flex flex-col min-h-screen bg-gray-950">
      <PageMeta
        title="Privacy Policy And Data Use | skore.info"
        description="Read the skore.info privacy policy, including local preference storage, limited usage data collection, and support contact handling."
        canonicalPath="/privacy"
        keywords={['privacy policy', 'skore.info', 'sports app privacy']}
      />
      <Header />
      
      <main className="container mx-auto px-4 py-8 flex-grow">
        <h1 className="text-3xl md:text-4xl font-bold text-white mb-8">Privacy Policy</h1>
        
        <div className="max-w-4xl mx-auto bg-gray-900/80 p-8 rounded-xl border border-gray-700">
          <div className="prose prose-invert max-w-none">
            <p className="mb-4 text-gray-300">Last Updated: {new Date().toLocaleDateString()}</p>
            
            <section className="mb-6">
              <h2 className="text-xl font-semibold mb-3 text-white">Introduction</h2>
              <p className="text-gray-300">
                This Privacy Policy describes how {companyName} ("we," "our," or "us") collects, uses, and shares information
                when you use our website and services. We are committed to protecting your privacy and ensuring a safe experience.
              </p>
            </section>
            
            <section className="mb-6">
              <h2 className="text-xl font-semibold mb-3 text-white">Information We Collect</h2>
              <p className="mb-3 text-gray-300">
                Our application is designed with minimal data collection. We do not require user registration or login. We only collect:
              </p>
              <ul className="list-disc pl-6 mb-3 text-gray-300">
                <li>Sport preferences and spoiler-safe replay plans stored locally on your device</li>
                <li>Usage data and analytics information about route visits, filter changes, score reveals, recap clicks, and guide-to-sport clicks (non-personally identifiable)</li>
                <li>Device and browser information for optimal performance</li>
              </ul>
              <p className="text-gray-300">
                Preference and replay-plan data is stored locally on your device using browser storage. Native reminder details stay in the device's local notification system. Analytics scripts are not loaded and analytics events are not sent until you allow analytics.
              </p>
            </section>
            
            <section className="mb-6">
              <h2 className="text-xl font-semibold mb-3 text-white">How We Use Your Information</h2>
              <p className="mb-2 text-gray-300">We use the information we collect to:</p>
              <ul className="list-disc pl-6 mb-3 text-gray-300">
                <li>Provide and maintain our services</li>
                <li>Remember your filtering preferences</li>
                <li>Provide optional local, spoiler-free replay reminders that you request</li>
                <li>Improve user experience</li>
                <li>Monitor and analyze usage patterns</li>
                <li>Develop new features</li>
              </ul>
            </section>
            
            <section className="mb-6">
              <h2 className="text-xl font-semibold mb-3 text-white">Cookies and Local Storage</h2>
              <p className="text-gray-300">
                We use cookies and local storage to save your preferences (like sport selection and filter settings) and your private replay plan.
                These help maintain your settings between visits but don't contain personally identifiable information.
                You can clear these from your browser settings at any time.
              </p>
            </section>
            
            <section className="mb-6">
              <h2 className="text-xl font-semibold mb-3 text-white">Data Sharing</h2>
              <p className="text-gray-300">
                We do not sell, trade, or otherwise transfer your information to third parties.
                We may use analytics providers to help us understand application usage, but these
                services only receive anonymized, aggregate-style event data without names, account identifiers, or spoiler details such as final scores or winners.
              </p>
            </section>
            
            <section className="mb-6">
              <h2 className="text-xl font-semibold mb-3 text-white">Your Rights</h2>
              <p className="mb-3 text-gray-300">You have the right to:</p>
              <ul className="list-disc pl-6 mb-3 text-gray-300">
                <li>Clear your browser cookies and local storage, or reset app data and reminders from Settings</li>
                <li>Use our service without accepting cookies (though some features may be limited)</li>
                <li>Request information about what data we have stored</li>
              </ul>
            </section>
            
            <section className="mb-6">
              <h2 className="text-xl font-semibold mb-3 text-white">Changes to This Policy</h2>
              <p className="text-gray-300">
                We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page
                and updating the "Last Updated" date. We recommend checking this page periodically for any changes.
              </p>
            </section>
            
            <section className="mb-6">
              <h2 className="text-xl font-semibold mb-3 text-white">External Links</h2>
              <p className="text-gray-300">
                Our website may contain links to external sites that are not operated by us. Please be aware that we have no control over 
                the content and practices of these sites, and cannot accept responsibility or liability for their respective privacy policies 
                or practices. We encourage you to review the privacy policies of any external sites you visit via links on our website.
              </p>
            </section>
            
            <section>
              <h2 className="text-xl font-semibold mb-3 text-white">Contact Us</h2>
              <p className="text-gray-300">
                If you have any questions about this Privacy Policy, please contact us at {supportEmailNew}.
              </p>
            </section>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default PrivacyPolicy;
